import { useMutation, useQueryClient } from '@tanstack/react-query';

// The retrieve WRITE («Recuperado» — the courier took the parcel out of the «buzón»), sibling of
// useConfirmDeliveryCollect, useEnableDelivery, useNonCollectDelivery and useRejectDelivery. The
// retrieve fn is injected (DI, MSW-testable). retry: false — a WRITE that force-transitions state
// must never be silently re-sent. On success, invalidate the detail (so the card refetches the new
// retrieved state, data-driven) AND the user-deliveries list by prefix — the hook does not know the
// userId, so the default exact: false matches every ['user-deliveries', *], keeping the list
// coherent when the agent returns to it. The code-agnostic refetch-on-error lives in DeliveryLayout,
// not here.
export function useRetrieveDelivery(id: string, retrieveDelivery: (id: string) => Promise<void>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => retrieveDelivery(id),
    retry: false,
    // Fire-and-forget: do NOT return the promise. Returning it makes TanStack await the refetch
    // before the mutation settles, which would hide the refetch skeleton the caller drives off the
    // query's own fetching state.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery-detail', id] });
      void queryClient.invalidateQueries({ queryKey: ['user-deliveries'] });
      // …and the same list reached from a building's tab: same rows, other entry point.
      void queryClient.invalidateQueries({ queryKey: ['location-deliveries'] });
      // The three box-level reads below are invalidated by PREFIX because they are keyed by BOX or
      // LOCK id, which this hook does not know — and unlike the other delivery writes, none of the
      // three is a precaution here: this write records that the «buzón» was physically emptied.
      // The box log gains the retrieval, so a re-unfold revalidates.
      void queryClient.invalidateQueries({ queryKey: ['locker-log'] });
      // The box-status notice must refetch too: the parcel LEFT, so the occupancy really changed.
      void queryClient.invalidateQueries({ queryKey: ['box-status'] });
      // …and the lock codes, for the same reason: the physical opening behind this registration may
      // have consumed a code sequence and moved the pointer, so the carriles must not keep painting
      // the sequence read before it.
      void queryClient.invalidateQueries({ queryKey: ['lock-codes'] });
    },
  });
}
